import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BrowserRouter as Router } from 'react-router-dom';

import AppRoutes from './components/AppRoutes';
import { SessionOperationContext } from './lib/SessionOperationContext';
import { SessionPresentationContext } from './lib/SessionPresentationContext';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { ensureCsrfToken } from './lib/csrf';
import { installSessionLifecycle } from './lib/sessionLifecycle';

import HelpCenter from "./components/HelpCenter";
import { HelpContextProvider } from "./help/HelpContext";
import { AccessProvider } from './access/AccessContext';
import { EMPTY_ACCESS, normalizeAccess } from './access/capabilities';


function App() {
  const MySwal = withReactContent(Swal);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [access, setAccess] = useState(EMPTY_ACCESS);
  const [sessionLost, setSessionLost] = useState(false);
  const [sessionState, setSessionState] = useState(null);
  const lifecycle = useRef(null);
  const sessionGeneration = useRef(0);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    const handler = installSessionLifecycle(axios, () => {
      sessionGeneration.current += 1;
      setAccess(EMPTY_ACCESS);
      setIsAuthenticated(false);
      setSessionLost(true);
      setAuthLoading(false);
    }, {
      onChange: setSessionState,
      onSessionVerified: (data) => setAccess(normalizeAccess(data)),
    });
    lifecycle.current = handler;
    return () => {
      mounted.current = false;
      sessionGeneration.current += 1;
      handler.dispose();
    };
  }, []);

  const loadSession = async (bootstrap = false) => {
    const generation = ++sessionGeneration.current;
    lifecycle.current?.beginTransition();
    const started = performance.now();
    const current = () => mounted.current && sessionGeneration.current === generation;
    try {
      const response = await axios.get('/api/check_session/', { withCredentials: true });
      if (!current()) return;
      const nextAccess = normalizeAccess(response.data);
      lifecycle.current?.setAuthenticated(nextAccess.authenticated, response.data, started);
      setAccess(nextAccess);
      setIsAuthenticated(nextAccess.authenticated);
      if (nextAccess.authenticated) setSessionLost(false);
    } catch (error) {
      if (!current()) return;
      if (!bootstrap) throw error;
      lifecycle.current?.setAuthenticated(false);
      setAccess(EMPTY_ACCESS);
      setIsAuthenticated(false);
    } finally {
      if (current()) setAuthLoading(false);
    }
  };

  const handleLoginSuccess = async () => {
    await loadSession();
  };

  const handleLogout = async () => {
    const generation = ++sessionGeneration.current;
    lifecycle.current?.beginTransition();
    const current = () => mounted.current && sessionGeneration.current === generation;
    try {
      const csrfToken = await ensureCsrfToken();
      if (!current()) return;

      await axios.post(
        '/api/logout/',
        {},
        {
          withCredentials: true,
          headers: {
            'X-CSRFToken': csrfToken,
          },
        }
      );

      if (!current()) return;

      localStorage.clear();
      lifecycle.current?.setAuthenticated(false);
      setSessionLost(false);
      setIsAuthenticated(false);
      setAccess(EMPTY_ACCESS);
      setAuthLoading(false);

      await MySwal.fire({
        icon: 'success',
        title: 'You have been signed out.',
        text: '',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK',
      });
    } catch (err) {
      if (!current()) return;
      const confirmation = await lifecycle.current?.resumeAfterLogoutFailure();
      if (!current() || confirmation?.authenticated === false) return;
      console.error('Error signing out:', err);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Unable to sign out.',
      });
    }
  };

  /*  
  useEffect(() => {
    axios
      .get('http://localhost:8000/api/check_session/', {
        withCredentials: true,
      })
      .then(() => setIsAuthenticated(true))
      .catch(() => setIsAuthenticated(false))
      .finally(() => setAuthLoading(false));
  }, []);

  */

  useEffect(() => {
    loadSession(true);
    // Bootstrap once; successful login performs an explicit authoritative refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (authLoading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  const operationSessionGeneration = sessionGeneration.current;
  // Callable compatibility for existing consumers, with a fenced async contract
  // for deliberate, still-unsent operations. Reauthentication invalidates it.
  const operationReady = () => operationReady.isCurrent()
    && Boolean(lifecycle.current?.isOperationReady());
  operationReady.generation = operationSessionGeneration;
  operationReady.isCurrent = () => mounted.current && isAuthenticated
    && operationSessionGeneration === sessionGeneration.current;
  operationReady.ensureReady = async () => {
    if (!operationReady.isCurrent()) return false;
    const ready = await lifecycle.current?.ensureOperationReady();
    return Boolean(ready && operationReady());
  };
  return (
    <Router>
      <AccessProvider access={access}>
       <HelpContextProvider>
        <div className={isAuthenticated ? 'session-app-shell flex h-screen supports-[height:100dvh]:h-dvh min-h-0 w-full flex-col overflow-hidden' : undefined}>
        {sessionLost && <div role="alert">Your session has expired or is no longer valid. Please sign in again. Your failed operation was not retried.</div>}
        <SessionPresentationContext.Provider value={isAuthenticated ? {
          state: sessionState, onStaySignedIn: () => lifecycle.current?.renew(),
          onRecheck: () => lifecycle.current?.reconcile(), onSignOut: handleLogout,
        } : null}>

        <div className={isAuthenticated ? 'session-route-shell min-h-0 min-w-0 flex-1 overflow-hidden' : undefined}>
        <SessionOperationContext.Provider value={operationReady}>
        <AppRoutes
          isAuthenticated={isAuthenticated}
          onLogin={handleLoginSuccess}
          onLogout={handleLogout}
        />
        </SessionOperationContext.Provider>
        </div>
        </SessionPresentationContext.Provider>
        </div>

        {isAuthenticated && <HelpCenter />}
       </HelpContextProvider>
      </AccessProvider>
    </Router>
  );
}

export default App;
